// Up Next card — pins the single next-starting game across the user's
// current tracked sports (and filters), with a live countdown.

import React, { useEffect, useMemo, useState } from "react";
import { CalendarPlus, Tv, Zap } from "lucide-react";
import { platformInfo } from "@/lib/streamers";
import { useAffiliateLinks, affiliateUrlFor } from "@/lib/affiliates";
import { trackAffiliateClick } from "@/lib/affiliateTracking";
import { gameToIcs, downloadIcs, isExportable } from "@/lib/ics";
import FavoriteButton from "@/components/FavoriteButton";

const ACCENT = "#067647";

function parseStart(game) {
  if (!game?.start_iso) return null;
  try {
    const d = new Date(game.start_iso);
    return isNaN(d.getTime()) ? null : d;
  } catch { return null; }
}

function pickNextGame(games) {
  const now = Date.now();
  let best = null;
  let bestDelta = Infinity;
  for (const g of games || []) {
    const d = parseStart(g);
    if (!d) continue;
    const delta = d.getTime() - now;
    // In-progress or finished games are not "next". Allow a 10-min grace
    // window for games that just kicked off so refresh timing feels fluid.
    if (delta < -10 * 60 * 1000) continue;
    if (delta < bestDelta) {
      bestDelta = delta;
      best = g;
    }
  }
  return best;
}

function formatCountdown(target) {
  const now = Date.now();
  const t = target.getTime();
  const diff = t - now;
  if (diff <= 0) return "STARTING NOW";
  const totalMin = Math.floor(diff / 60000);
  const days = Math.floor(totalMin / (60 * 24));
  const hours = Math.floor((totalMin - days * 60 * 24) / 60);
  const mins = totalMin - days * 60 * 24 - hours * 60;
  if (days > 0) return `${days}d ${hours}h ${mins}m`;
  if (hours > 0) return `${hours}h ${mins}m`;
  return `${mins}m`;
}

function formatStartLabel(target, tz) {
  try {
    return new Intl.DateTimeFormat(undefined, {
      timeZone: tz || undefined,
      weekday: "short", month: "short", day: "numeric",
      hour: "numeric", minute: "2-digit",
    }).format(target);
  } catch {
    return target.toLocaleString();
  }
}

export default function UpNextCard({ games, timezone, testid = "up-next-card" }) {
  const next = useMemo(() => pickNextGame(games), [games]);
  const affiliateLinks = useAffiliateLinks();
  const [tick, setTick] = useState(0);

  // Refresh the countdown every 30 seconds while mounted.
  useEffect(() => {
    if (!next) return;
    const id = setInterval(() => setTick((n) => n + 1), 30000);
    return () => clearInterval(id);
  }, [next]);

  if (!next) return null;
  const startAt = parseStart(next);
  if (!startAt) return null;

  // Reference the tick so React re-runs the countdown render.
  void tick;

  const platforms = (next.platforms && next.platforms.length > 0)
    ? next.platforms
    : (next.platform ? [next.platform] : []);
  const countdown = formatCountdown(startAt);
  const startLabel = formatStartLabel(startAt, timezone);
  const live = countdown === "STARTING NOW";
  const exportable = isExportable(next);

  return (
    <div
      className="mb-8 border-2 border-[#111] bg-[#111] text-white relative overflow-hidden"
      data-testid={testid}
    >
      <div className="absolute inset-0 opacity-[0.07] pointer-events-none"
           style={{ background: "repeating-linear-gradient(45deg, #fff 0, #fff 1px, transparent 1px, transparent 10px)" }} />
      <div className="relative p-5 sm:p-6">
        <div className="flex items-center justify-between gap-3 mb-4">
          <div className="inline-flex items-center gap-2 font-mono text-[10px] tracking-[0.4em] uppercase font-extrabold text-[#aaa]">
            <Zap size={12} strokeWidth={3} style={{ color: ACCENT }} />
            Up next
          </div>
          <div
            className="font-display text-xl sm:text-2xl font-extrabold tracking-tight tabular-nums"
            style={{ color: live ? ACCENT : "#fff" }}
            data-testid={`${testid}-countdown`}
          >
            {countdown}
          </div>
        </div>

        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div className="min-w-0 flex-1">
            <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#aaa] font-bold mb-1.5">
              {next.sport} · {startLabel}
            </div>
            <div
              className="font-display text-2xl sm:text-3xl md:text-4xl font-extrabold leading-tight tracking-tight"
              data-testid={`${testid}-matchup`}
            >
              <span className="inline-flex items-center gap-2">
                {next.away_team}
                <FavoriteButton
                  sport={next.sport}
                  team={next.away_team}
                  testid={`${testid}-fav-away`}
                  size={16}
                />
              </span>
              <span className="mx-3 inline-block opacity-40 font-mono text-base align-middle">@</span>
              <span className="inline-flex items-center gap-2">
                {next.home_team}
                <FavoriteButton
                  sport={next.sport}
                  team={next.home_team}
                  testid={`${testid}-fav-home`}
                  size={16}
                />
              </span>
            </div>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            {platforms.length > 0 ? (
              platforms.map((p, i) => {
                const info = platformInfo(p);
                const href = affiliateUrlFor(info.label, affiliateLinks);
                const shared = {
                  className: "streamer",
                  style: { background: info.color, textDecoration: "none" },
                  "data-testid": `${testid}-platform-${i}`,
                  title: href ? `Watch on ${info.label}` : info.label,
                };
                return href ? (
                  <a key={`${i}-${info.label}`} href={href} target="_blank" rel="sponsored noopener noreferrer"
                     onClick={() => trackAffiliateClick({
                       partner: info.label,
                       kind: "streamer",
                       context: "up-next",
                       sport: next.sport,
                       home_team: next.home_team,
                       away_team: next.away_team,
                     })} {...shared}>
                    <span className="dot" />
                    {info.label}
                  </a>
                ) : (
                  <span key={`${i}-${info.label}`} {...shared}>
                    <span className="dot" />
                    {info.label}
                  </span>
                );
              })
            ) : (
              <span className="inline-flex items-center gap-1.5 font-mono text-[10px] tracking-[0.2em] uppercase font-bold text-[#aaa]">
                <Tv size={12} strokeWidth={2.4} />
                Streaming TBD
              </span>
            )}
            {exportable && (
              <button
                type="button"
                onClick={() => {
                  const ics = gameToIcs(next);
                  if (!ics) return;
                  const fname = `${next.away_team}-at-${next.home_team}-${(next.date || "").slice(0, 10)}`;
                  downloadIcs(fname, ics);
                }}
                className="inline-flex items-center gap-1.5 px-3 py-2 font-mono text-[10px] tracking-[0.2em] uppercase font-extrabold border-2 border-white text-white hover:bg-white hover:text-[#111] transition-colors focus-ring"
                data-testid={`${testid}-add-calendar`}
                title="Add this game to your calendar"
              >
                <CalendarPlus size={13} strokeWidth={2.6} />
                Add to calendar
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
